import { supabase } from '../supabase';
import { Event, Task, CalendarAccount, EmailTemplate } from '../types/calendar';

export interface CalendarEvent {
  id: string;
  title: string;
  description?: string;
  location?: string;
  start: Date;
  end: Date;
  allDay?: boolean;
  color?: string;
  module: string;
  reminder_minutes?: number;
  recurrence_rule?: string;
  created_at: string;
  updated_at: string;
  user_id: string;
  // Legacy fields for backward compatibility
  start_time?: string;
  end_time?: string;
  all_day?: boolean;
}

export interface CalendarTask {
  id: string;
  title: string;
  description?: string;
  completed: boolean;
  priority: 'low' | 'medium' | 'high';
  due_date?: string;
  category: string;
  subtasks: Subtask[];
  created_at: string;
  user_id: string;
}

export interface Subtask {
  id: string;
  title: string;
  completed: boolean;
  task_id: string;
}

export interface CalendarAccount {
  id: string;
  name: string;
  type: 'google' | 'outlook' | 'apple' | 'ics';
  email: string;
  connected: boolean;
  last_sync?: string;
  color: string;
  user_id: string;
}

export interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  body: string;
  user_id: string;
}

class CalendarService {
  // Events
  async getEvents(userId: string, startDate?: Date, endDate?: Date): Promise<CalendarEvent[]> {
    let query = supabase
      .from('calendar_events')
      .select('*')
      .eq('user_id', userId)
      .order('start_time', { ascending: true });

    if (startDate && endDate) {
      query = query.gte('start_time', startDate.toISOString())
                   .lte('end_time', endDate.toISOString());
    }

    const { data, error } = await query;
    
    if (error) {
      console.error('Error fetching events:', error);
      throw error;
    }

    // Convert to new format
    return (data || []).map(event => ({
      ...event,
      start: new Date(event.start_time),
      end: new Date(event.end_time),
      allDay: event.all_day,
      color: event.color || '#10B981'
    }));
  }

  async createEvent(event: Omit<CalendarEvent, 'id' | 'created_at' | 'updated_at'>): Promise<CalendarEvent> {
    const eventData = {
      title: event.title,
      description: event.description,
      location: event.location,
      start_time: event.start.toISOString(),
      end_time: event.end.toISOString(),
      all_day: event.allDay || false,
      color: event.color || '#10B981',
      module: event.module,
      reminder_minutes: event.reminder_minutes,
      recurrence_rule: event.recurrence_rule,
      user_id: event.user_id,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    const { data, error } = await supabase
      .from('calendar_events')
      .insert([eventData])
      .select()
      .single();

    if (error) {
      console.error('Error creating event:', error);
      throw error;
    }

    // Convert to new format
    return {
      ...data,
      start: new Date(data.start_time),
      end: new Date(data.end_time),
      allDay: data.all_day,
      color: data.color || '#10B981'
    };
  }

  async updateEvent(id: string, updates: Partial<CalendarEvent>): Promise<CalendarEvent> {
    const updateData: any = {
      ...updates,
      updated_at: new Date().toISOString()
    };

    // Convert new format fields to database format
    if (updates.start) {
      updateData.start_time = updates.start.toISOString();
    }
    if (updates.end) {
      updateData.end_time = updates.end.toISOString();
    }
    if (updates.allDay !== undefined) {
      updateData.all_day = updates.allDay;
    }

    // Remove the new format fields from updateData
    delete updateData.start;
    delete updateData.end;
    delete updateData.allDay;

    const { data, error } = await supabase
      .from('calendar_events')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating event:', error);
      throw error;
    }

    // Convert to new format
    return {
      ...data,
      start: new Date(data.start_time),
      end: new Date(data.end_time),
      allDay: data.all_day,
      color: data.color || '#10B981'
    };
  }

  async deleteEvent(id: string): Promise<void> {
    const { error } = await supabase
      .from('calendar_events')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting event:', error);
      throw error;
    }
  }

  // Tasks
  async getTasks(userId: string, filter?: 'all' | 'active' | 'completed'): Promise<CalendarTask[]> {
    let query = supabase
      .from('calendar_tasks')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (filter === 'active') {
      query = query.eq('completed', false);
    } else if (filter === 'completed') {
      query = query.eq('completed', true);
    }

    const { data, error } = await query;
    
    if (error) {
      console.error('Error fetching tasks:', error);
      throw error;
    }

    return data || [];
  }

  async createTask(task: Omit<CalendarTask, 'id' | 'created_at'>): Promise<CalendarTask> {
    const { data, error } = await supabase
      .from('calendar_tasks')
      .insert([{
        ...task,
        created_at: new Date().toISOString()
      }])
      .select()
      .single();

    if (error) {
      console.error('Error creating task:', error);
      throw error;
    }

    return data;
  }

  async updateTask(id: string, updates: Partial<CalendarTask>): Promise<CalendarTask> {
    const { data, error } = await supabase
      .from('calendar_tasks')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating task:', error);
      throw error;
    }

    return data;
  }

  async deleteTask(id: string): Promise<void> {
    const { error } = await supabase
      .from('calendar_tasks')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting task:', error);
      throw error;
    }
  }

  // Subtasks
  async getSubtasks(taskId: string): Promise<Subtask[]> {
    const { data, error } = await supabase
      .from('calendar_subtasks')
      .select('*')
      .eq('task_id', taskId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error fetching subtasks:', error);
      throw error;
    }

    return data || [];
  }

  async createSubtask(subtask: Omit<Subtask, 'id'>): Promise<Subtask> {
    const { data, error } = await supabase
      .from('calendar_subtasks')
      .insert([subtask])
      .select()
      .single();

    if (error) {
      console.error('Error creating subtask:', error);
      throw error;
    }

    return data;
  }

  async updateSubtask(id: string, updates: Partial<Subtask>): Promise<Subtask> {
    const { data, error } = await supabase
      .from('calendar_subtasks')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating subtask:', error);
      throw error;
    }

    return data;
  }

  async deleteSubtask(id: string): Promise<void> {
    const { error } = await supabase
      .from('calendar_subtasks')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting subtask:', error);
      throw error;
    }
  }

  // Calendar Accounts
  async getCalendarAccounts(userId: string): Promise<CalendarAccount[]> {
    const { data, error } = await supabase
      .from('calendar_accounts')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching calendar accounts:', error);
      throw error;
    }

    return data || [];
  }

  async createCalendarAccount(account: Omit<CalendarAccount, 'id'>): Promise<CalendarAccount> {
    const { data, error } = await supabase
      .from('calendar_accounts')
      .insert([account])
      .select()
      .single();

    if (error) {
      console.error('Error creating calendar account:', error);
      throw error;
    }

    return data;
  }

  async updateCalendarAccount(id: string, updates: Partial<CalendarAccount>): Promise<CalendarAccount> {
    const { data, error } = await supabase
      .from('calendar_accounts')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating calendar account:', error);
      throw error;
    }

    return data;
  }

  async deleteCalendarAccount(id: string): Promise<void> {
    const { error } = await supabase
      .from('calendar_accounts')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting calendar account:', error);
      throw error;
    }
  }

  // Email Templates
  async getEmailTemplates(userId: string): Promise<EmailTemplate[]> {
    const { data, error } = await supabase
      .from('email_templates')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching email templates:', error);
      throw error;
    }

    return data || [];
  }

  async createEmailTemplate(template: Omit<EmailTemplate, 'id'>): Promise<EmailTemplate> {
    const { data, error } = await supabase
      .from('email_templates')
      .insert([template])
      .select()
      .single();

    if (error) {
      console.error('Error creating email template:', error);
      throw error;
    }

    return data;
  }

  async updateEmailTemplate(id: string, updates: Partial<EmailTemplate>): Promise<EmailTemplate> {
    const { data, error } = await supabase
      .from('email_templates')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating email template:', error);
      throw error;
    }

    return data;
  }

  async deleteEmailTemplate(id: string): Promise<void> {
    const { error } = await supabase
      .from('email_templates')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting email template:', error);
      throw error;
    }
  }

  // Module Events Aggregation
  async getModuleEvents(userId: string, modules: string[], startDate?: Date, endDate?: Date): Promise<CalendarEvent[]> {
    // This would aggregate events from different modules (health, pets, travel, etc.)
    // For now, we'll just get calendar events and filter by module
    const events = await this.getEvents(userId, startDate, endDate);
    return events.filter(event => modules.includes(event.module));
  }

  // Real-time subscriptions
  subscribeToEvents(userId: string, callback: (payload: any) => void) {
    return supabase
      .channel('calendar_events')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'calendar_events',
        filter: `user_id=eq.${userId}`
      }, callback)
      .subscribe();
  }

  subscribeToTasks(userId: string, callback: (payload: any) => void) {
    return supabase
      .channel('calendar_tasks')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'calendar_tasks',
        filter: `user_id=eq.${userId}`
      }, callback)
      .subscribe();
  }

  // ICS Export
  generateICS(events: CalendarEvent[]): string {
    let icsContent = 'BEGIN:VCALENDAR\r\n';
    icsContent += 'VERSION:2.0\r\n';
    icsContent += 'PRODID:-//iDecide//Calendar//EN\r\n';

    events.forEach(event => {
      icsContent += 'BEGIN:VEVENT\r\n';
      icsContent += `UID:${event.id}@idecide.com\r\n`;
      icsContent += `DTSTAMP:${new Date().toISOString().replace(/[-:]/g, '').split('.')[0]}Z\r\n`;
      icsContent += `DTSTART:${event.start_time.replace(/[-:]/g, '').split('.')[0]}Z\r\n`;
      icsContent += `DTEND:${event.end_time.replace(/[-:]/g, '').split('.')[0]}Z\r\n`;
      icsContent += `SUMMARY:${event.title}\r\n`;
      if (event.description) {
        icsContent += `DESCRIPTION:${event.description}\r\n`;
      }
      if (event.location) {
        icsContent += `LOCATION:${event.location}\r\n`;
      }
      icsContent += 'END:VEVENT\r\n';
    });

    icsContent += 'END:VCALENDAR\r\n';
    return icsContent;
  }

  // ICS Import
  async importICS(icsContent: string, userId: string): Promise<CalendarEvent[]> {
    const events: CalendarEvent[] = [];
    const lines = icsContent.split('\r\n');
    let currentEvent: Partial<CalendarEvent> = {};
    let inEvent = false;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      if (line === 'BEGIN:VEVENT') {
        inEvent = true;
        currentEvent = {};
      } else if (line === 'END:VEVENT') {
        if (currentEvent.title && currentEvent.start_time && currentEvent.end_time) {
          const event: Omit<CalendarEvent, 'id' | 'created_at' | 'updated_at'> = {
            title: currentEvent.title,
            description: currentEvent.description,
            location: currentEvent.location,
            start_time: currentEvent.start_time,
            end_time: currentEvent.end_time,
            all_day: currentEvent.all_day || false,
            color: currentEvent.color || '#10B981',
            module: 'calendar',
            user_id: userId
          };
          
          try {
            const createdEvent = await this.createEvent(event);
            events.push(createdEvent);
          } catch (error) {
            console.error('Error importing event:', error);
          }
        }
        inEvent = false;
      } else if (inEvent) {
        const [key, value] = line.split(':');
        switch (key) {
          case 'SUMMARY':
            currentEvent.title = value;
            break;
          case 'DESCRIPTION':
            currentEvent.description = value;
            break;
          case 'LOCATION':
            currentEvent.location = value;
            break;
          case 'DTSTART':
            currentEvent.start_time = this.parseICSDate(value);
            break;
          case 'DTEND':
            currentEvent.end_time = this.parseICSDate(value);
            break;
        }
      }
    }

    return events;
  }

  private parseICSDate(dateString: string): string {
    // Convert ICS date format to ISO string
    const year = dateString.substring(0, 4);
    const month = dateString.substring(4, 6);
    const day = dateString.substring(6, 8);
    const hour = dateString.substring(9, 11);
    const minute = dateString.substring(11, 13);
    const second = dateString.substring(13, 15);
    
    return `${year}-${month}-${day}T${hour}:${minute}:${second}.000Z`;
  }
}

export const calendarService = new CalendarService(); 